import {openConfirmDialog, openDialog, showNotification} from 'platform/components';

import {isNil} from 'ramda';

import {useLazyGetBusinessCaseQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError, SendCustomerEmailForm} from '@dms/shared';

import {isBelowPriorityRating} from '../../model/auctionView';
import type {AuctionOffer, AuctionView} from '../../model/auctionView.types';
import {useAuctionFormat} from '../components/useAuctionFormat';

interface ConfirmWinnerOptions {
  vehicleId: string;
  vehicleTitle: string;
  view: AuctionView;
  isOwnStock: boolean;
  onConfirm: (offerId: string) => Promise<AuctionView | null>;
}

// Confirms the winner, then opens the platform email form for the buyer. The seller can open the form again later.
export function useConfirmWinner(options: ConfirmWinnerOptions) {
  const format = useAuctionFormat();
  const [getBusinessCase] = useLazyGetBusinessCaseQuery();

  // The BE opens the sale business case with the buyer as its customer, so the platform email form can send to the buyer.
  const notifyBuyer = async (offer: AuctionOffer, view: AuctionView) => {
    const saleBusinessCaseId = view.transaction?.saleBusinessCaseId;
    // Without the sale case the email cannot open; the seller creates the case from SaleCasePanel.
    if (isNil(saleBusinessCaseId)) {
      showNotification.warning(i18n.t('entity.auction.confirmation.saleCase.failed'));
      return;
    }
    const businessCase = await getBusinessCase({businessCaseId: saleBusinessCaseId})
      .unwrap()
      .catch(handleApiError);
    if (!businessCase) {
      return;
    }
    openDialog(
      <SendCustomerEmailForm
        customerId={businessCase.customerId}
        recordId={saleBusinessCaseId}
        resourceId={EntityResourceIds.businessCase}
        vehicleId={options.vehicleId}
        defaultValues={{
          customerEmailAddress: offer.traderEmail,
          subject: i18n.t('entity.auction.notifyWinner.defaultSubject', {
            vehicle: options.vehicleTitle,
            round: options.view.roundNumber ?? 1,
          }),
          body: i18n.t(
            options.isOwnStock
              ? 'entity.auction.notifyWinner.defaultMessageOwnStock'
              : 'entity.auction.notifyWinner.defaultMessage',
            {vehicle: options.vehicleTitle, price: format.money(offer.amount)}
          ),
        }}
      />,
      {
        id: 'auction-notify-winner-dialog',
        'data-testid': 'auction-notify-winner-dialog',
        withAdditionalFooter: true,
        size: 'large',
        title: i18n.t('general.actions.sendEmail'),
      }
    );
  };

  const confirmWinner = (offer: AuctionOffer) => {
    const higherOffer = options.view.offers.find(
      (other) =>
        other.rejectedAt === null &&
        other.amount.currency === offer.amount.currency &&
        Number(other.amount.amount) > Number(offer.amount.amount)
    );
    const summary = i18n.t('entity.auction.notifyWinner.confirmText', {
      buyer: [offer.traderName, offer.traderCompany].filter(Boolean).join(' · '),
      price: format.money(offer.amount),
      defaultValue:
        'Confirm {buyer} as the buyer for {price}. The sale is concluded and you cannot withdraw the vehicle. Then you send the notification email to the buyer.',
    });
    const warnings = [
      higherOffer &&
        i18n.t('entity.auction.notifyWinner.higherOffer', {
          dealer: higherOffer.traderName,
          amount: format.money(higherOffer.amount),
          difference: format.money({
            amount: (Number(higherOffer.amount.amount) - Number(offer.amount.amount)).toFixed(2),
            currency: higherOffer.amount.currency,
          }),
        }),
      isBelowPriorityRating(offer) &&
        i18n.t('entity.auction.notifyWinner.lowRating', {
          defaultValue:
            'This buyer is rated below 3 stars and lost priority. The second-best bidder can be taken instead.',
        }),
    ];
    openConfirmDialog({
      id: 'auction-confirm-winner-dialog',
      text: [summary, ...warnings].filter(Boolean).join(' '),
      onConfirm: async () => {
        const view = await options.onConfirm(offer.offerId);
        if (view !== null) {
          await notifyBuyer(offer, view);
        }
      },
      'data-testid': 'auction-confirm-winner-dialog',
    });
  };

  return {confirmWinner, notifyBuyer: (offer: AuctionOffer) => notifyBuyer(offer, options.view)};
}
